// ============================================
// TextbookSidebarReader.jsx - Sidebar Textbook Reader
// ============================================
// Optimized for the right sidebar - reading & study tools
// Features: Read chapters, summarize, quiz, flashcards, ask questions

import React, { useState, useEffect, useRef } from 'react';
import {
  BookOpen,
  X,
  ChevronLeft,
  ChevronRight,
  Layers,
  GraduationCap,
  MessageSquare,
  Send,
  Loader,
  Sparkles,
  Brain,
  FileQuestion,
  ListChecks,
  Network,
  Lightbulb,
  BookMarked,
  Search,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  RefreshCw,
  ArrowLeft,
  MoreHorizontal,
  Play,
  Pause,
} from 'lucide-react';
import { textbookService } from '../services/textbookService';

// ============================================
// STUDY TOOLS CONFIG
// ============================================
const STUDY_TOOLS = [
  {
    id: 'summarize',
    label: 'Summary',
    icon: Sparkles,
    color: '#8b5cf6',
    prompt: `Summarize this chapter content in clear, simple terms for a student.
Include:
- Main thesis or argument
- Key concepts (3-5 bullet points)
- Important takeaways

Keep it concise but comprehensive.`,
  },
  {
    id: 'quiz',
    label: 'Quiz',
    icon: FileQuestion,
    color: '#f59e0b',
    responseType: 'quiz', // Special handling
    prompt: `Create a 5-question quiz based on this chapter content.

Format your response EXACTLY like this:
Q1: [Question text]
A) [Option A]
B) [Option B]
C) [Option C]
D) [Option D]
ANSWER: [Correct letter]

Q2: [Question text]
...and so on for all 5 questions.`,
  },
  {
    id: 'flashcards',
    label: 'Cards',
    icon: ListChecks,
    color: '#10b981',
    responseType: 'flashcards', // Special handling
    prompt: `Create 8 flashcards for the key concepts in this chapter.

Format your response EXACTLY like this:
CARD 1
TERM: [Term or concept]
DEFINITION: [Clear definition or explanation]

CARD 2
TERM: [Term or concept]
DEFINITION: [Clear definition or explanation]

...and so on for all 8 cards.`,
  },
  {
    id: 'eli5',
    label: 'Simple',
    icon: Lightbulb,
    color: '#ec4899',
    prompt: `Explain the main concepts of this chapter like you're explaining to a 10-year-old.

Use:
- Simple words (avoid jargon)
- Real-world examples and analogies
- Short sentences
- A friendly, encouraging tone

Make it easy to understand!`,
  },
];

// ============================================
// CHAPTER SELECTOR DROPDOWN
// ============================================
function ChapterSelector({ chapters, currentChapter, onSelect, loading }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentIndex = chapters.findIndex(c => c.name === currentChapter?.name);

  return (
    <div className="chapter-selector" ref={dropdownRef}>
      <button 
        className="chapter-selector-btn"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading || chapters.length === 0}
      >
        <Layers size={14} />
        <span className="chapter-current">
          {loading ? 'Loading...' : currentChapter?.name || 'Select Chapter'}
        </span>
        <ChevronDown size={14} className={isOpen ? 'rotated' : ''} />
      </button>
      
      {isOpen && (
        <div className="chapter-dropdown">
          {chapters.map((chapter, idx) => (
            <button
              key={idx}
              className={`chapter-option ${currentChapter?.name === chapter.name ? 'active' : ''}`}
              onClick={() => {
                onSelect(chapter);
                setIsOpen(false);
              }}
            >
              <span className="chapter-num">{idx + 1}</span>
              <span className="chapter-name">{chapter.name}</span>
            </button>
          ))}
        </div>
      )}
      
      {/* Quick nav arrows */}
      <div className="chapter-nav-arrows">
        <button 
          onClick={() => currentIndex > 0 && onSelect(chapters[currentIndex - 1])}
          disabled={currentIndex <= 0 || loading}
          title="Previous chapter"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="chapter-progress">{currentIndex + 1}/{chapters.length}</span>
        <button 
          onClick={() => currentIndex < chapters.length - 1 && onSelect(chapters[currentIndex + 1])}
          disabled={currentIndex >= chapters.length - 1 || loading}
          title="Next chapter"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}

// ============================================
// READING CONTENT VIEW
// ============================================
function ReadingView({ chunks, chapter, loading }) {
  const contentRef = useRef(null);

  if (loading) {
    return (
      <div className="reading-loading">
        <Loader className="spinning" size={20} />
        <span>Loading chapter...</span>
      </div>
    );
  }

  if (!chunks || chunks.length === 0) {
    return (
      <div className="reading-empty">
        <BookOpen size={28} />
        <p>Select a chapter to start reading</p>
      </div>
    );
  }

  return (
    <div className="reading-content" ref={contentRef}>
      <h3 className="reading-title">{chapter?.name}</h3>
      {chapter?.page_start && (
        <div className="reading-meta">
          Pages {chapter.page_start} - {chapter.page_end} • {chunks.length} sections
        </div>
      )}
      <div className="reading-text">
        {chunks.map((chunk, idx) => (
          <div key={idx} className="reading-section">
            <p>{chunk.content}</p>
            <span className="page-marker">p. {chunk.page_start || chunk.page}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// STUDY TOOL RESULT VIEW
// ============================================
function ToolResultView({ tool, result, loading, onClose, onRegenerate }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const ToolIcon = tool?.icon || Sparkles;

  return (
    <div className="tool-result-view">
      <div className="tool-result-header">
        <div className="tool-result-title">
          <ToolIcon size={16} style={{ color: tool?.color }} />
          <span>{tool?.label}</span>
        </div>
        <div className="tool-result-actions">
          <button onClick={onRegenerate} disabled={loading} title="Regenerate">
            <RefreshCw size={14} className={loading ? 'spinning' : ''} />
          </button>
          <button onClick={handleCopy} title="Copy">
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
          <button onClick={onClose} title="Back to reading">
            <X size={14} />
          </button>
        </div>
      </div>
      
      <div className="tool-result-body">
        {loading ? (
          <div className="tool-loading">
            <Loader className="spinning" size={20} />
            <span>Generating {tool?.label?.toLowerCase()}...</span>
          </div>
        ) : (
          <div className="tool-text">{result}</div>
        )}
      </div>
    </div>
  );
}

// ============================================
// ASK CHAT VIEW
// ============================================
function AskView({ textbook, chapter, onBack }) {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || loading) return;

    const userMsg = query.trim();
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setQuery('');
    setLoading(true);

    try {
      // Use enhanced query for better answers
      const result = await textbookService.enhancedQuery(
        textbook.id, 
        userMsg,
        chapter?.name,  // Pass chapter context
        5,
        true  // Include related concepts
      );
      
      if (result.error) {
        // Fallback to basic query
        const fallbackResult = await textbookService.queryTextbook(
          textbook.id,
          chapter ? `About "${chapter.name}": ${userMsg}` : userMsg,
          'student',
          true
        );
        
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: fallbackResult.data?.answer || 'Sorry, I couldn\'t find an answer.',
          sources: fallbackResult.data?.context 
        }]);
      } else {
        // Format enhanced response
        let answer = result.data?.answer || '';
        
        // If no direct answer, we need to generate one from results
        if (!answer && result.data?.results?.length > 0) {
          const context = result.data.results.map(r => 
            `[${r.source_type}, p.${r.page}] ${r.content}`
          ).join('\n\n');
          
          // Generate answer using context
          const genResult = await textbookService.queryTextbook(
            textbook.id,
            `Based on this context, answer the question: "${userMsg}"\n\nContext:\n${context}`,
            'student',
            true
          );
          answer = genResult.data?.answer || 'I found some relevant information but couldn\'t formulate an answer.';
        }
        
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: answer || 'Sorry, I couldn\'t find an answer.',
          sources: result.data?.results?.slice(0, 3),
          relatedConcepts: result.data?.related_concepts,
          queryVariations: result.data?.query_variations,
        }]);
      }
    } catch (e) {
      console.error('Query failed:', e);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Error processing question. Please try again.',
        error: true 
      }]);
    }
    setLoading(false);
  };

  const suggestions = [
    'Explain the main ideas',
    'Give me examples',
    'What should I remember?',
  ];

  return (
    <div className="ask-view">
      <div className="ask-header">
        <button onClick={onBack} className="back-btn">
          <ArrowLeft size={16} />
        </button>
        <span>Ask about {chapter ? `"${chapter.name}"` : 'this textbook'}</span>
      </div>
      
      <div className="ask-messages">
        {messages.length === 0 ? (
          <div className="ask-empty">
            <MessageSquare size={24} />
            <p>Ask any question about the textbook</p>
            <div className="ask-suggestions">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => setQuery(s)}>{s}</button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div key={idx} className={`ask-message ${msg.role}`}>
              <div className="message-bubble">{msg.content}</div>
              {msg.sources?.length > 0 && (
                <div className="message-sources">
                  {msg.sources.slice(0, 3).map((s, i) => (
                    <span key={i} className="source-tag">
                      {s.source_type === 'definition' ? '📖' : '📄'} p.{s.page || s.page_start}
                    </span>
                  ))}
                </div>
              )}
              {msg.relatedConcepts?.length > 0 && (
                <div className="message-concepts">
                  <span className="concepts-label">Related:</span>
                  {msg.relatedConcepts.slice(0, 3).map((c, i) => (
                    <span key={i} className="concept-tag" title={c.definition}>
                      {c.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
        {loading && (
          <div className="ask-message assistant">
            <div className="message-bubble loading">
              <Loader className="spinning" size={14} />
              <span>Thinking...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <form onSubmit={handleSubmit} className="ask-input">
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ask a question..."
          disabled={loading}
        />
        <button type="submit" disabled={!query.trim() || loading}>
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}

// ============================================
// DEFINITIONS VIEW
// ============================================
function DefinitionsView({ definitions, loading, onBack }) {
  const [search, setSearch] = useState('');

  const filtered = definitions.filter(d =>
    d.term?.toLowerCase().includes(search.toLowerCase()) ||
    d.definition?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="definitions-view">
      <div className="definitions-header">
        <button onClick={onBack} className="back-btn">
          <ArrowLeft size={16} />
        </button>
        <span>Key Terms ({definitions.length})</span>
      </div>
      
      <div className="definitions-search">
        <Search size={14} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search terms..."
        />
      </div>
      
      <div className="definitions-list">
        {loading ? (
          <div className="definitions-loading">
            <Loader className="spinning" size={18} />
          </div>
        ) : filtered.length > 0 ? (
          filtered.map((def, idx) => (
            <div key={idx} className="definition-card">
              <div className="def-term">{def.term}</div>
              <div className="def-content">{def.definition || def.content}</div>
              {def.page && <div className="def-page">p. {def.page}</div>}
            </div>
          ))
        ) : (
          <div className="definitions-empty">
            {search ? 'No matching terms' : 'No definitions found'}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// MAIN SIDEBAR READER COMPONENT
// ============================================
export default function TextbookSidebarReader({ textbook, onClose }) {
  // Data state
  const [chapters, setChapters] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [chapterChunks, setChapterChunks] = useState([]);
  
  // Loading states
  const [loadingInit, setLoadingInit] = useState(true);
  const [loadingChunks, setLoadingChunks] = useState(false);
  const [loadingTool, setLoadingTool] = useState(false);
  
  // View state: 'read' | 'ask' | 'definitions' | 'tool'
  const [activeView, setActiveView] = useState('read');
  const [activeTool, setActiveTool] = useState(null);
  const [toolResult, setToolResult] = useState('');

  // Load initial data
  useEffect(() => {
    if (textbook?.id) {
      loadTextbookData();
    }
  }, [textbook?.id]);

  const loadTextbookData = async () => {
    setLoadingInit(true);
    try {
      const [chaptersRes, defsRes] = await Promise.all([
        textbookService.getChapters(textbook.id),
        textbookService.getDefinitions(textbook.id),
      ]);

      const chapterList = chaptersRes.data?.chapters || [];
      setChapters(chapterList);
      setDefinitions(defsRes.data?.definitions || []);

      if (chapterList.length > 0) {
        selectChapter(chapterList[0]);
      }
    } catch (e) {
      console.error('Failed to load textbook:', e);
    }
    setLoadingInit(false);
  };

  const selectChapter = async (chapter) => {
    setCurrentChapter(chapter);
    setLoadingChunks(true);
    setActiveView('read');
    setActiveTool(null);
    
    try {
      const result = await textbookService.searchTextbook(textbook.id, chapter.name, 15);
      setChapterChunks(result.data?.results || []);
    } catch (e) {
      console.error('Failed to load chapter:', e);
      setChapterChunks([]);
    }
    setLoadingChunks(false);
  };

  const handleStudyTool = async (tool) => {
    setActiveTool(tool);
    setActiveView('tool');
    setLoadingTool(true);
    setToolResult('');

    try {
      // Get chapter content for context
      const chapterContent = chapterChunks.map(c => c.content).join('\n\n').slice(0, 6000);
      
      // First, try enhanced query to get better context
      let context = chapterContent;
      
      try {
        const enhancedResult = await textbookService.enhancedQuery(
          textbook.id,
          `Key information about: ${currentChapter?.name}`,
          currentChapter?.name,
          10,
          true
        );
        
        if (enhancedResult.data?.results?.length > 0) {
          // Use re-ranked results as context (better quality)
          context = enhancedResult.data.results
            .map(r => r.content)
            .join('\n\n')
            .slice(0, 6000);
        }
      } catch (e) {
        console.log('Enhanced query failed, using raw chunks:', e);
      }
      
      // Now generate the study tool content with the context
      const result = await textbookService.queryTextbook(
        textbook.id,
        `${tool.prompt}\n\nChapter: ${currentChapter?.name}\n\nContent:\n${context}`,
        'student',
        true
      );
      
      if (result.data?.answer) {
        setToolResult(result.data.answer);
      } else if (result.error) {
        setToolResult(`Error: ${result.error}`);
      } else {
        setToolResult('Could not generate content. Please try again.');
      }
    } catch (e) {
      console.error('Study tool error:', e);
      setToolResult(`Failed to generate: ${e.message || 'Unknown error'}`);
    }
    setLoadingTool(false);
  };

  if (loadingInit) {
    return (
      <div className="sidebar-reader loading">
        <Loader className="spinning" size={24} />
        <span>Loading textbook...</span>
      </div>
    );
  }

  return (
    <div className="sidebar-reader">
      {/* Header */}
      <div className="reader-header">
        <button className="back-btn" onClick={onClose}>
          <ArrowLeft size={18} />
        </button>
        <div className="reader-title">
          <BookMarked size={16} style={{ color: '#8b5cf6' }} />
          <span>{textbook.title}</span>
        </div>
      </div>

      {/* Chapter Selector */}
      <ChapterSelector
        chapters={chapters}
        currentChapter={currentChapter}
        onSelect={selectChapter}
        loading={loadingChunks}
      />

      {/* Study Tools Bar */}
      {activeView === 'read' && currentChapter && (
        <div className="tools-bar">
          {STUDY_TOOLS.map(tool => {
            const Icon = tool.icon;
            return (
              <button
                key={tool.id}
                className="tool-btn"
                onClick={() => handleStudyTool(tool)}
                style={{ '--tool-color': tool.color }}
                title={tool.label}
              >
                <Icon size={14} />
                <span>{tool.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Main Content Area */}
      <div className="reader-content">
        {activeView === 'read' ? (
          <ReadingView 
            chunks={chapterChunks} 
            chapter={currentChapter}
            loading={loadingChunks}
          />
        ) : activeView === 'tool' ? (
          <ToolResultView
            tool={activeTool}
            result={toolResult}
            loading={loadingTool}
            onClose={() => setActiveView('read')}
            onRegenerate={() => handleStudyTool(activeTool)}
          />
        ) : activeView === 'ask' ? (
          <AskView
            textbook={textbook}
            chapter={currentChapter}
            onBack={() => setActiveView('read')}
          />
        ) : activeView === 'definitions' ? (
          <DefinitionsView
            definitions={definitions}
            loading={false}
            onBack={() => setActiveView('read')}
          />
        ) : null}
      </div>

      {/* Bottom Navigation */}
      {activeView === 'read' && (
        <div className="reader-bottom-nav">
          <button 
            className="nav-btn"
            onClick={() => setActiveView('definitions')}
          >
            <GraduationCap size={16} />
            <span>Terms</span>
          </button>
          <button 
            className="nav-btn primary"
            onClick={() => setActiveView('ask')}
          >
            <MessageSquare size={16} />
            <span>Ask</span>
          </button>
        </div>
      )}

      {/* Styles */}
      <style>{`
        .sidebar-reader {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: var(--gi-gray-900, #111827);
          color: var(--gi-gray-100, #f3f4f6);
        }

        .sidebar-reader.loading {
          align-items: center;
          justify-content: center;
          gap: 12px;
          color: var(--gi-gray-500);
        }

        /* Header */
        .reader-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border-bottom: 1px solid var(--gi-gray-800);
        }

        .back-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: transparent;
          border: none;
          border-radius: 6px;
          color: var(--gi-gray-400);
          cursor: pointer;
        }

        .back-btn:hover {
          background: var(--gi-gray-800);
          color: var(--gi-gray-200);
        }

        .reader-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
          flex: 1;
          min-width: 0;
        }

        .reader-title span {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Chapter Selector */
        .chapter-selector {
          padding: 8px 12px;
          border-bottom: 1px solid var(--gi-gray-800);
          position: relative;
        }

        .chapter-selector-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 8px 10px;
          background: var(--gi-gray-800);
          border: 1px solid var(--gi-gray-700);
          border-radius: 6px;
          color: var(--gi-gray-200);
          font-size: 12px;
          cursor: pointer;
          text-align: left;
        }

        .chapter-selector-btn:hover {
          border-color: var(--gi-gray-600);
        }

        .chapter-current {
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .chapter-selector-btn svg.rotated {
          transform: rotate(180deg);
        }

        .chapter-dropdown {
          position: absolute;
          top: 100%;
          left: 12px;
          right: 12px;
          background: var(--gi-gray-800);
          border: 1px solid var(--gi-gray-700);
          border-radius: 8px;
          max-height: 250px;
          overflow-y: auto;
          z-index: 100;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }

        .chapter-option {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 12px;
          background: transparent;
          border: none;
          color: var(--gi-gray-300);
          font-size: 12px;
          text-align: left;
          cursor: pointer;
        }

        .chapter-option:hover {
          background: var(--gi-gray-700);
        }

        .chapter-option.active {
          background: rgba(139, 92, 246, 0.15);
          color: #a78bfa;
        }

        .chapter-num {
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--gi-gray-700);
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
        }

        .chapter-option.active .chapter-num {
          background: #8b5cf6;
          color: white;
        }

        .chapter-name {
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .chapter-nav-arrows {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 8px;
        }

        .chapter-nav-arrows button {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--gi-gray-800);
          border: none;
          border-radius: 4px;
          color: var(--gi-gray-400);
          cursor: pointer;
        }

        .chapter-nav-arrows button:hover:not(:disabled) {
          background: var(--gi-gray-700);
          color: var(--gi-gray-200);
        }

        .chapter-nav-arrows button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .chapter-progress {
          font-size: 11px;
          color: var(--gi-gray-500);
        }

        /* Tools Bar */
        .tools-bar {
          display: flex;
          gap: 6px;
          padding: 8px 12px;
          border-bottom: 1px solid var(--gi-gray-800);
          overflow-x: auto;
        }

        .tool-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 6px 10px;
          background: var(--gi-gray-800);
          border: none;
          border-radius: 6px;
          color: var(--gi-gray-400);
          font-size: 11px;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s;
        }

        .tool-btn:hover {
          background: var(--gi-gray-700);
          color: var(--tool-color, var(--gi-gray-200));
        }

        /* Reader Content */
        .reader-content {
          flex: 1;
          overflow-y: auto;
          min-height: 0;
        }

        /* Reading View */
        .reading-loading, .reading-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 200px;
          gap: 10px;
          color: var(--gi-gray-500);
          font-size: 13px;
        }

        .reading-content {
          padding: 16px;
        }

        .reading-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--gi-gray-100);
          margin: 0 0 6px 0;
        }

        .reading-meta {
          font-size: 11px;
          color: var(--gi-gray-500);
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--gi-gray-800);
        }

        .reading-text {
          font-size: 13px;
          line-height: 1.7;
          color: var(--gi-gray-300);
        }

        .reading-section {
          margin-bottom: 16px;
          position: relative;
        }

        .reading-section p {
          margin: 0;
        }

        .page-marker {
          display: block;
          font-size: 9px;
          color: var(--gi-gray-600);
          margin-top: 4px;
          text-align: right;
        }

        /* Tool Result View */
        .tool-result-view {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .tool-result-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px;
          border-bottom: 1px solid var(--gi-gray-800);
        }

        .tool-result-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 600;
        }

        .tool-result-actions {
          display: flex;
          gap: 4px;
        }

        .tool-result-actions button {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--gi-gray-800);
          border: none;
          border-radius: 4px;
          color: var(--gi-gray-400);
          cursor: pointer;
        }

        .tool-result-actions button:hover {
          background: var(--gi-gray-700);
          color: var(--gi-gray-200);
        }

        .tool-result-body {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
        }

        .tool-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 150px;
          gap: 10px;
          color: var(--gi-gray-500);
        }

        .tool-text {
          font-size: 13px;
          line-height: 1.7;
          color: var(--gi-gray-300);
          white-space: pre-wrap;
        }

        /* Ask View */
        .ask-view {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .ask-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border-bottom: 1px solid var(--gi-gray-800);
          font-size: 13px;
          font-weight: 500;
        }

        .ask-messages {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
        }

        .ask-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          gap: 10px;
          color: var(--gi-gray-500);
          text-align: center;
        }

        .ask-empty p {
          margin: 0;
          font-size: 13px;
        }

        .ask-suggestions {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 12px;
        }

        .ask-suggestions button {
          padding: 6px 12px;
          background: var(--gi-gray-800);
          border: none;
          border-radius: 16px;
          color: var(--gi-gray-300);
          font-size: 11px;
          cursor: pointer;
        }

        .ask-suggestions button:hover {
          background: var(--gi-gray-700);
        }

        .ask-message {
          margin-bottom: 12px;
        }

        .ask-message.user {
          text-align: right;
        }

        .message-bubble {
          display: inline-block;
          max-width: 85%;
          padding: 10px 14px;
          border-radius: 12px;
          font-size: 13px;
          line-height: 1.5;
        }

        .ask-message.user .message-bubble {
          background: #8b5cf6;
          color: white;
          border-bottom-right-radius: 4px;
        }

        .ask-message.assistant .message-bubble {
          background: var(--gi-gray-800);
          color: var(--gi-gray-200);
          border-bottom-left-radius: 4px;
          text-align: left;
        }

        .message-bubble.loading {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--gi-gray-400);
        }

        .message-sources {
          display: flex;
          gap: 6px;
          margin-top: 6px;
          font-size: 10px;
          color: var(--gi-gray-500);
          flex-wrap: wrap;
        }

        .message-sources span,
        .source-tag {
          padding: 2px 6px;
          background: var(--gi-gray-800);
          border-radius: 3px;
        }

        .message-concepts {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-top: 6px;
          flex-wrap: wrap;
        }

        .concepts-label {
          font-size: 10px;
          color: var(--gi-gray-500);
        }

        .concept-tag {
          padding: 2px 8px;
          background: rgba(139, 92, 246, 0.15);
          color: #a78bfa;
          border-radius: 10px;
          font-size: 10px;
          cursor: help;
        }

        .concept-tag:hover {
          background: rgba(139, 92, 246, 0.25);
        }

        .ask-input {
          display: flex;
          gap: 8px;
          padding: 12px;
          border-top: 1px solid var(--gi-gray-800);
        }

        .ask-input input {
          flex: 1;
          padding: 10px 12px;
          background: var(--gi-gray-800);
          border: 1px solid var(--gi-gray-700);
          border-radius: 8px;
          color: var(--gi-gray-100);
          font-size: 13px;
        }

        .ask-input input:focus {
          outline: none;
          border-color: #8b5cf6;
        }

        .ask-input button {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #8b5cf6;
          border: none;
          border-radius: 8px;
          color: white;
          cursor: pointer;
        }

        .ask-input button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Definitions View */
        .definitions-view {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .definitions-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          border-bottom: 1px solid var(--gi-gray-800);
          font-size: 13px;
          font-weight: 500;
        }

        .definitions-search {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-bottom: 1px solid var(--gi-gray-800);
          color: var(--gi-gray-500);
        }

        .definitions-search input {
          flex: 1;
          background: none;
          border: none;
          color: var(--gi-gray-200);
          font-size: 12px;
        }

        .definitions-search input:focus {
          outline: none;
        }

        .definitions-list {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
        }

        .definitions-loading, .definitions-empty {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100px;
          color: var(--gi-gray-500);
          font-size: 12px;
        }

        .definition-card {
          padding: 10px 12px;
          background: var(--gi-gray-800);
          border-radius: 8px;
          margin-bottom: 8px;
        }

        .def-term {
          color: #a78bfa;
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .def-content {
          color: var(--gi-gray-300);
          font-size: 11px;
          line-height: 1.5;
        }

        .def-page {
          color: var(--gi-gray-500);
          font-size: 9px;
          margin-top: 4px;
        }

        /* Bottom Nav */
        .reader-bottom-nav {
          display: flex;
          gap: 8px;
          padding: 10px 12px;
          border-top: 1px solid var(--gi-gray-800);
        }

        .nav-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px;
          background: var(--gi-gray-800);
          border: none;
          border-radius: 8px;
          color: var(--gi-gray-300);
          font-size: 12px;
          cursor: pointer;
        }

        .nav-btn:hover {
          background: var(--gi-gray-700);
        }

        .nav-btn.primary {
          background: #8b5cf6;
          color: white;
        }

        .nav-btn.primary:hover {
          background: #7c3aed;
        }

        /* Animations */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .spinning {
          animation: spin 1s linear infinite;
        }

        /* Scrollbar */
        .sidebar-reader ::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-reader ::-webkit-scrollbar-track {
          background: transparent;
        }

        .sidebar-reader ::-webkit-scrollbar-thumb {
          background: var(--gi-gray-700);
          border-radius: 3px;
        }
      `}</style>
    </div>
  );
}
